import { createDealerAxios } from 'src/factories/axios-factory';
import {AutoTraderValuation, CapValuationResponse, Valuation } from 'src/types/valuations';
import type { AutoTraderAmount, AutoTraderValuationResponse } from 'src/types/valuations/auto-trader-valuation';

const dealerBackendDealer = createDealerAxios(process.env.DealersApiUrl!);

const valuationService = {

  getValuations(affiliateId: number): Promise<Valuation[]> {
    const affId = affiliateId.toString();
    return new Promise<Valuation[]>((resolve, reject) => {
      dealerBackendDealer.get(`/valuations/${affId}`)
        .then(res => {
          if (res.status === 200) {
            // Use the setStatus function
            const valuations = setStatus(res.data as Valuation[]);
            resolve(valuations);
          } else {
            console.log("No valuations found!");
            reject("NO_VALUATIONS_FOUND");
          }
        })
        .catch(error => {
          console.log("Error fetching valuations:", error);
          reject("ERROR_FETCHING_VALUATIONS");
        });
    });
  },
  capValuation(capId: number, mileage: number, reg: string, year: number | undefined) {
    return new Promise<CapValuationResponse | null>((resolve, reject) => {
      dealerBackendDealer.get(
        `/cap-valuations?capId=${capId}&mileage=${mileage}&vehicleRegistration=${reg.toUpperCase().replace(" ", "")}&year=${year}`).then(res => {
          if (res.status === 200) {
            resolve(res.data as CapValuationResponse);
          }
        }).catch(error => {
          console.log("no valuations found!");
          resolve(null)
        })
    })
  },
  saveValuation(valuation: Valuation): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await dealerBackendDealer.post(
          `/valuation`, 
          valuation);        
        if (res.status === 200) {
          valuation = setStatus([valuation])[0];
          resolve(valuation); 
        } else {
          reject(new Error('Failed to save valuation'));
        }
      } catch (error) {
        reject(error);
      }
    });
  },
getAutoTraderValuation(aid: number, registration: string, odometerReadingMiles: number): Promise<AutoTraderValuation> {
  return new Promise<AutoTraderValuation>((resolve, reject) => {
    dealerBackendDealer.post(
      `/v3/valuations/autotrader`,
      {
        affiliateUserId: aid,
        vrm: registration,
        currentMileage: odometerReadingMiles,
      }
    )
    .then(res => {
      console.log("Auto Trader API response:", res);
      if (res.status === 200) {
        resolve(normaliseAutoTraderValuation(res.data as AutoTraderValuationResponse, registration, odometerReadingMiles));
      } else {
        reject("NO_AUTOTRADER_VALUATION_FOUND");
      }
    })
    .catch(error => {
      console.log("Error fetching Auto Trader valuation:", error);
      reject("ERROR_FETCHING_AUTOTRADER_VALUATION");
    });
  });
},
};

// Resolves a single Auto Trader figure. Cars expose the value on amountGBP;
// VAT-qualifying vans leave amountGBP null, so fall back to the VAT split.
function pickAutoTraderAmount(node: AutoTraderAmount | number | null | undefined): number | undefined {
  if (node === null || node === undefined) return undefined;
  if (typeof node === 'number') return node;
  return node.amountGBP ?? node.amountNoVatGBP ?? node.amountExcludingVatGBP ?? undefined;
}

// A figure is "commercial" when Auto Trader omits amountGBP but still returns a
// VAT split, which is how it signals a VAT-qualifying vehicle such as a van.
function isCommercialAmount(node: AutoTraderAmount | number | null | undefined): boolean {
  if (!node || typeof node === 'number') return false;
  return (node.amountGBP === null || node.amountGBP === undefined) &&
    (node.amountNoVatGBP != null || node.amountExcludingVatGBP != null);
}

// Flattens the Auto Trader payload (legacy flat shape or nested per-figure shape)
// into the numbers the rest of the app and UI already consume.
function normaliseAutoTraderValuation(
  data: AutoTraderValuationResponse,
  registration: string,
  currentMileage: number
): AutoTraderValuation {
  const figures = data.valuations ?? data;

  const result: AutoTraderValuation = {
    vrm: registration,
    currentMileage,
    retail: pickAutoTraderAmount(figures.retail),
    partExchange: pickAutoTraderAmount(figures.partExchange),
    trade: pickAutoTraderAmount(figures.trade),
    private: pickAutoTraderAmount(figures.private),
  };

  if ([figures.retail, figures.partExchange, figures.trade, figures.private].some(isCommercialAmount)) {
    result.isCommercial = true;
  }

  return result;
}

function setStatus(valuations: Valuation[]): Valuation[] {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  return valuations.map(valuation => {
    if (valuation.created) {
      const createdDate = new Date(valuation.created);
      const isSameMonth =
        createdDate.getMonth() === currentMonth &&
        createdDate.getFullYear() === currentYear;

      valuation.status = isSameMonth ? 1 : -1;
    } else {
      valuation.status = -1; // Handle null `created` dates
    }

    return valuation;
  });
}

export default valuationService;
